import type { PersonLeaderboardRow } from "./types";

export type SortKey =
  | "person"
  | "teamsRemaining"
  | "goalDifference"
  | "goalsFor"
  | "goalsAgainst"
  | "points";

export type SortDirection = "asc" | "desc";

export type SortRule = {
  key: SortKey;
  direction: SortDirection;
};

export const defaultPersonLeaderboardSort: SortRule[] = [
  { key: "teamsRemaining", direction: "desc" },
  { key: "goalDifference", direction: "desc" },
  { key: "goalsFor", direction: "desc" },
  { key: "points", direction: "desc" },
  { key: "person", direction: "asc" },
];

export const columnDefaultDirection: Record<SortKey, SortDirection> = {
  person: "asc",
  teamsRemaining: "desc",
  goalDifference: "desc",
  goalsFor: "desc",
  goalsAgainst: "asc",
  points: "desc",
};

function compareValues(
  a: string | number | null | undefined,
  b: string | number | null | undefined,
  direction: SortDirection
): number {
  const multiplier = direction === "asc" ? 1 : -1;

  const aMissing = a === null || a === undefined;
  const bMissing = b === null || b === undefined;
  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;

  if (typeof a === "string" && typeof b === "string") {
    return a.localeCompare(b) * multiplier;
  }

  return ((a as number) - (b as number)) * multiplier;
}

export function sortPersonLeaderboard(
  rows: PersonLeaderboardRow[],
  primarySort?: SortRule
): PersonLeaderboardRow[] {
  const rules = primarySort
    ? [
        primarySort,
        ...defaultPersonLeaderboardSort.filter(
          (rule) => rule.key !== primarySort.key
        ),
      ]
    : defaultPersonLeaderboardSort;

  return [...rows].sort((a, b) => {
    for (const rule of rules) {
      const result = compareValues(a[rule.key], b[rule.key], rule.direction);
      if (result !== 0) return result;
    }
    return 0;
  });
}

export function nextSortRule(
  current: SortRule | null,
  clickedKey: SortKey
): SortRule {
  if (current && current.key === clickedKey) {
    return {
      key: clickedKey,
      direction: current.direction === "asc" ? "desc" : "asc",
    };
  }
  return { key: clickedKey, direction: columnDefaultDirection[clickedKey] };
}

export function assignRanks(
  rows: PersonLeaderboardRow[]
): (PersonLeaderboardRow & { rank: number })[] {
  const sorted = sortPersonLeaderboard(rows);

  const out: (PersonLeaderboardRow & { rank: number })[] = [];
  for (let i = 0; i < sorted.length; i++) {
    const row = sorted[i];
    if (i === 0) {
      out.push({ ...row, rank: 1 });
      continue;
    }
    const prev = sorted[i - 1];
    const tied =
      prev.teamsRemaining === row.teamsRemaining &&
      prev.goalDifference === row.goalDifference &&
      prev.goalsFor === row.goalsFor &&
      prev.points === row.points;
    const prevRank = out[i - 1].rank;
    out.push({ ...row, rank: tied ? prevRank : i + 1 });
  }
  return out;
}
